import React, {useState, useEffect} from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ReferenceLine, Tooltip, Legend, ResponsiveContainer, Label } from 'recharts';
import { convertSingleValue, japaneseTickFormatter } from '../utils/commonUtils';
import AxisSettingButton from '../utils/AxisSettingButton';

const BarGraph = ({ data, graph, height=500, radius=[0, 0, 0, 0], width=null}) => {
    const [showYaxis, setShowYaxis] = useState(false);
    const [state, setState] = useState('auto');
    const [isSmallArea, setIsSmallArea] = useState(false);

    const stopColor = [
        { start: 'rgba(145, 186, 250, 1)', end: 'rgba(80, 140, 200, 1)' }, // 青
        { start: 'rgba(225, 97, 50, 1)', end: 'rgba(255, 120, 90, 1)' }, // オレンジ
        { start: 'rgba(90, 195, 90, 1)', end: 'rgba(50, 110, 50, 1)' }, // 緑
        { start: 'rgba(147, 112, 219, 1)', end: 'rgba(75, 0, 130, 1)' },  // 紫
        { start: 'rgba(255, 235, 59, 1)', end: 'rgba(251, 192, 45, 1)' }, // 黄色
    ];

    const CustomLabel = ({ x, y, width, value, index, seriesIndex }) => {
        const offset = value >= 0 ? -10 : 20;
        return (
            <text
              x={x + width / 2}
              y={y + offset} 
              fill="black"
              textAnchor="middle"
              fontSize={14}
            >
              {data[index].labels[seriesIndex]}  {/* ラベルの表示 */}
            </text>
        );
    };

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            return (
                <div className="custom-tooltip px-3 py-2 border border-gray-300 bg-gray-600 rounded shadow text-white">
                {payload.map((item, index) => {
                    const seriesIndex = parseInt(item.dataKey.replace('y[', '').replace(']', ''));
                    const labelColor = stopColor[seriesIndex]?.start || 'rgba(100, 100, 100, 0.7)';
                    return (
                        <div key={index} className="flex items-center space-x-2">
                            <svg width="12" height="12" className="flex-shrink-0">
                                <rect
                                    width="12"
                                    height="12"
                                    fill={labelColor}
                                    rx="2"
                                />
                            </svg>
                            <div key={index} className='white'>
                                {`${graph.names[index]} ${item.payload.x}: ${item.payload.labels[index]}`}
                            </div>
                        </div>
                    );
                })}
                </div>
            );
        }
        return null;
    };
      
    const formatTick = (tick) => {
        return convertSingleValue(tick);
    }

    const handleLabelSettingClick = (newState) => {
        setState(newState);
    }

    useEffect(() => {
        if (!width) {
            setIsSmallArea(false);
        } else {
            if (data) {
                if (width / (data.length * data[0].y.length) < 80 ) {
                    setIsSmallArea(true);
                } else {
                    setIsSmallArea(false);
                }
            }
        }
    }, [width, data]);

    useEffect(() => {
        if ((state === 'auto' && isSmallArea) || state==='hidden')  { // autoで小さいときとhiddenのときは軸を表示
            setShowYaxis(true)
        } else {
            setShowYaxis(false)
        }
    }, [state, isSmallArea])
    
    return (
        <>
            <AxisSettingButton handleLabelSettingClick={handleLabelSettingClick} labelState={state} /> 
            <ResponsiveContainer width="100%" height={height}>
                <BarChart
                    data={data}
                    margin={{ top: 20, right: 30, left: 30, bottom: 40 }}
                    barCategoryGap="20%"
                    barGap={5}
                >
                {/* グラデーションの定義 */}
                <defs>
                    {stopColor.map((color, index) => (
                        <linearGradient id={`gradient${index}`} key={index} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={color.start} />
                            <stop offset="95%" stopColor={color.end} />
                        </linearGradient>
                    ))}
                </defs>
                { showYaxis&& <CartesianGrid strokeDasharray="3 3" horizontal={true} vertical={false} />}
                <XAxis
                    dataKey="x"
                    tick={{ fontSize: 12, fill: graph.settings.layout.xaxis.color }}>
                    <Label 
                        value={graph.settings.layout.xaxis.title.text}
                        position="bottom" 
                        offset={10}
                        style={{ textAnchor: 'middle', fill: graph.settings.layout.xaxis.color }}
                    />
                </XAxis>
                {showYaxis && 
                    <YAxis
                        tick={{ fontSize: 12, fill: graph.settings.layout.yaxis.color }}
                        tickCount={4}
                        tickFormatter={(value) => japaneseTickFormatter(value, '')}
                        >
                        <Label 
                            value={graph.settings.layout.yaxis.title.text} 
                            angle={-90}
                            position="left" 
                            offset={15}
                            style={{ textAnchor: 'middle', fill: graph.settings.layout.yaxis.color }}

                        />
                    </YAxis>
                }
                <ReferenceLine y={0} stroke="lightgray" />
                <Tooltip
                    content={<CustomTooltip />}
                    cursor={false}
                />
                <Legend verticalAlign="top" align="left" wrapperStyle={{ top: 0, display: "flex", flexWrap: "wrap" }} />
                {graph.y.map((y, seriesIndex) => (
                    <Bar
                    key={seriesIndex}
                    dataKey={`y[${seriesIndex}]`}
                    fill={`url(#gradient${seriesIndex % stopColor.length})`}  // グラデーションを適用
                    name={graph.names[seriesIndex]}
                    label={!showYaxis && <CustomLabel seriesIndex={seriesIndex} />} 
                    animationBegin={0}
                    animationDuration={1500}
                    animationEasing="ease-in-out"
                    radius={radius}
                    />
                ))}
                </BarChart>
            </ResponsiveContainer>
        </>
    );
};

export default BarGraph;
